/**
 * Canvas 2D map renderer — v0M2.4a code-only visual polish.
 *
 * Layered per MAP.md §8:
 *   1. Baked terrain (with per-cell value noise + dithered edges) — baked once per zoom step
 *   2. Political tint overlay (per-province colour at low alpha) — baked once per zoom step
 *   3. Province borders — baked once per zoom step
 *   4. Rivers — drawn per frame (world-space; thin geometry)
 *   5. Placeholder entities (city sites + a fake army) — drawn per frame
 *   6. HUD (map label, legend, zoom indicator, compass) — drawn per frame
 *
 * Later layers per MAP.md §8 (resources / cities / real armies /
 * engagement markers / fog) plug in as consuming milestones arrive.
 */

import type { LoadedMap } from './map-loader';
import type { Camera } from './camera';
import type { TileAtlas } from './tile-atlas';
import { pxPerCell, worldToScreen } from './camera';
import { computeBattleIconWorldPos } from '../hit-test';
import { terrainColor } from './terrain-palette';
import { applyNoiseToRgb } from './noise';
import { applyShadingToRgb, cellShading, cellElevationBrightness } from './hillshade';
import { provinceTint } from './faction-palette';
import { bakeProvinceBorders } from './border-render';
import { drawRivers } from './river-render';
import { ARM_N, ARM_E, ARM_S, ARM_W, type RiverTileAtlas } from './river-tile-atlas';
import { drawCityIcon, cityIconRadius, rgbToCss } from './city-icon';
import { drawMapLabel, drawLegend, drawZoomIndicator, drawCompass } from './hud';
import { drawFog, isCellVisibleToPlayer, isCellDetectedByPlayer } from './fog-render';
import { getFactionById } from '../../data/factions';
import { NEUTRAL_TEAM_ID } from '../../sim/scenario';
import type { GameState, FactionId } from '../../sim/state';
import { sortedIds } from '../../sim/state';
import { getDialNumber, loadDials } from '../../data/dials';

// MFix1.6 (2026-07-16) — ZoC circle radius in world cells. Same dial the
// sim consumes in zoc-detection.ts so the visual footprint matches the
// engagement-trigger footprint exactly.
const RENDERER_DIALS = loadDials();
const ZOC_RADIUS_CELLS = getDialNumber(RENDERER_DIALS, 'zoc_radius');
// MFix5+fix2 (2026-07-23) — city ZoC visual radius. Cities use Chebyshev
// distance (square outline) rather than Euclidean (circle). Matches
// sieges.ts::SIEGE_ADJACENCY_RADIUS = dial + 0.5 slop.
const CITY_ZOC_RADIUS_CELLS = getDialNumber(RENDERER_DIALS, 'settlement_zoc_radius');

// ---------------------------------------------------------------------------
// Baked layer cache — recomputed when zoom or map changes
// ---------------------------------------------------------------------------

export interface BakedLayers {
  terrain: HTMLCanvasElement;
  political: HTMLCanvasElement;
  borders: HTMLCanvasElement;
  /**
   * v0M14.3 (2026-07-29) — Tile-based river layer. Null when the
   * river atlas failed to load; caller falls back to the legacy
   * spline `drawRivers` overlay in that case.
   */
  rivers: HTMLCanvasElement | null;
  bakedZoom: number;
}

/**
 * Optional game-state context for the political tint layer + army overlay.
 * When absent (pre-menu smoke path or scenario not yet bootstrapped), the
 * political tint falls back to the placeholder province palette.
 *
 * `selectedArmyId` (v0M3.10) is the currently player-selected army — used
 * to draw the selection highlight ring + planned-path preview.
 */
export interface RenderState {
  readonly state: GameState;
  readonly playerFactionId: FactionId | null;
  readonly selectedArmyId?: import('../../sim/state').ArmyId | null;
  /** v0M5.3 (2026-07-25) — Currently selected Spy id (LMB on own spy). */
  readonly selectedSpyId?: import('../../sim/state').SpyId | null;
  /**
   * v0M7.5b (2026-07-26) — Accumulating Road-polyline waypoints for
   * the location-picker overlay. Non-null only while the player is
   * mid-pick (Build Road → LMBs). Renderer draws small numbered dots
   * at each waypoint and a dashed line between adjacent pairs.
   */
  readonly pendingRoadPolyline?: readonly { readonly x: number; readonly y: number }[] | null;
}

export function bakeAllLayers(
  map: LoadedMap,
  camera: Camera,
  atlas: TileAtlas | null = null,
  renderState: RenderState | null = null,
  riverAtlas: RiverTileAtlas | null = null,
): BakedLayers {
  return {
    terrain: bakeTerrainCanvas(map, camera, atlas),
    political: bakePoliticalCanvas(map, camera, renderState),
    borders: bakeProvinceBorders(map, camera),
    rivers: riverAtlas ? bakeRiversCanvas(map, camera, riverAtlas) : null,
    bakedZoom: pxPerCell(camera),
  };
}

/**
 * Bake the terrain grid into an offscreen canvas at the current zoom.
 *
 * v0M2.6e: when a tile atlas is available, each cell is filled from
 * the corresponding terrain tile via CanvasPattern (the tile shows
 * through at world-coordinate offset). No noise / hillshading is
 * applied — the painted tiles carry their own variance and shading.
 *
 * Fallback (atlas === null): reverts to the v0M2.6d palette + noise +
 * hillshading path so the map still renders even if tiles fail to
 * load.
 */
export function bakeTerrainCanvas(
  map: LoadedMap,
  camera: Camera,
  atlas: TileAtlas | null = null,
): HTMLCanvasElement {
  const z = pxPerCell(camera);
  const off = document.createElement('canvas');
  off.width = Math.ceil(map.width * z);
  off.height = Math.ceil(map.height * z);
  const ctx = off.getContext('2d');
  if (!ctx) throw new Error('bakeTerrainCanvas: offscreen 2D context unavailable');

  const NOISE_AMPLITUDE = 0.08;

  // Pass 1 — tiled or palette. Tiles preferred; palette is the fallback.
  // v0M12.1 (2026-07-26) — variant-aware: `atlas.pickVariant(id, cellIndex)`
  // deterministically picks among available tile variants for each cell
  // so adjacent same-terrain cells don't visibly repeat the same texture.
  if (atlas) {
    // Build per-context pattern arrays once (patterns are context-scoped).
    const patternArrays = atlas.patternsFor(ctx);
    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const cellIndex = y * map.width + x;
        const id = map.terrain[cellIndex]!;
        const variants = patternArrays[id];
        if (variants && variants.length > 0) {
          const vi = atlas.pickVariant(id, cellIndex);
          ctx.fillStyle = variants[vi]!;
        } else {
          const [pr, pg, pb] = terrainColor(id);
          ctx.fillStyle = `rgb(${pr}, ${pg}, ${pb})`;
        }
        ctx.fillRect(Math.floor(x * z), Math.floor(y * z), Math.ceil(z), Math.ceil(z));
      }
    }
  } else {
    for (let y = 0; y < map.height; y++) {
      for (let x = 0; x < map.width; x++) {
        const id = map.terrain[y * map.width + x]!;
        const base = terrainColor(id);
        const [nr, ng, nb] = applyNoiseToRgb(base[0], base[1], base[2], x, y, id, NOISE_AMPLITUDE);
        const slope = cellShading(map, x, y);
        const elevBrightness = cellElevationBrightness(map, x, y);
        const [r, g, b] = applyShadingToRgb(nr, ng, nb, slope, elevBrightness);
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.fillRect(Math.floor(x * z), Math.floor(y * z), Math.ceil(z), Math.ceil(z));
      }
    }
  }

  // Pass 2 — edge blending REMOVED (session 35 v0M12+art revert,
  // 2026-07-29). Both branches — MFix10.1's atlas-side stepped-alpha
  // tile-pattern blend and the v0M2.6d palette-side dither strip —
  // are removed. Perceptual benefit didn't earn its cost once tile
  // variety was in place; adjacent-different-terrain seams now show
  // as hard cell boundaries. Preserved in git history at commit
  // 5f6e09e^ (session 34 wrap) for reinstatement if needed.

  return off;
}

/**
 * Bake the political tint layer — a per-cell semi-transparent province
 * colour so ownership reads at a glance without dominating the terrain.
 *
 * With `renderState`: tint by owning faction's canonical color, with
 * alpha ramp — player = 0.42 (dominant), AI = 0.30 (visible), neutral =
 * 0.16 grey (subtle). Wilderness / unowned = uncolored.
 *
 * Without `renderState`: falls back to the placeholder `provinceTint`
 * palette (pre-scenario boot smoke path).
 */
export function bakePoliticalCanvas(
  map: LoadedMap,
  camera: Camera,
  renderState: RenderState | null = null,
): HTMLCanvasElement {
  const z = pxPerCell(camera);
  const off = document.createElement('canvas');
  off.width = Math.ceil(map.width * z);
  off.height = Math.ceil(map.height * z);
  const ctx = off.getContext('2d');
  if (!ctx) throw new Error('bakePoliticalCanvas: offscreen 2D context unavailable');

  const FALLBACK_ALPHA = 0.16;
  const NEUTRAL_RGB: readonly [number, number, number] = [140, 130, 110];

  // Precompute owner tint per province id when a state is provided so we
  // don't re-look-up per cell (100 provinces × 480×280 cells = 13M lookups
  // otherwise).
  const provinceTintLookup = renderState
    ? buildProvinceTintLookup(renderState, NEUTRAL_RGB)
    : null;

  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      // Skip sea cells — political tint over water reads wrong.
      const t = map.terrain[y * map.width + x]!;
      if (t === 8) continue;
      const pid = map.provinceMap[y * map.width + x]!;
      if (pid === 0) continue;

      let r: number, g: number, b: number, alpha: number;
      if (provinceTintLookup) {
        const entry = provinceTintLookup.get(pid);
        if (!entry) continue; // wilderness / unowned in the game state
        [r, g, b] = entry.color;
        alpha = entry.alpha;
      } else {
        [r, g, b] = provinceTint(pid);
        alpha = FALLBACK_ALPHA;
      }
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
      ctx.fillRect(Math.floor(x * z), Math.floor(y * z), Math.ceil(z), Math.ceil(z));
    }
  }
  return off;
}

/**
 * v0M14.3 (2026-07-29) — Bake the tile-based river layer.
 *
 * Iterates every River cell (terrain id 9) and, for each, computes a
 * 4-bit neighbour bitmask (N=1, E=2, S=4, W=8) of "which orthogonal
 * neighbour is also River". Looks up the matching authored tile via
 * `atlas.pickTile(bitmask)` and draws it at the cell rect.
 *
 * Per design lock (session 35 v0M14.0): sea/lake neighbours do NOT
 * count as River arms. Endpoints (1-arm cells) render via placeholder
 * fallback until source/mouth tiles land at v0M14.3a.
 *
 * The layer bakes to an offscreen canvas the same size as the terrain
 * layer, so the main render pipeline can blit it with a single
 * `drawImage` per frame. Rebuild triggers on zoom change only.
 */
export function bakeRiversCanvas(
  map: LoadedMap,
  camera: Camera,
  atlas: RiverTileAtlas,
): HTMLCanvasElement {
  const z = pxPerCell(camera);
  const off = document.createElement('canvas');
  off.width = Math.ceil(map.width * z);
  off.height = Math.ceil(map.height * z);
  const ctx = off.getContext('2d');
  if (!ctx) throw new Error('bakeRiversCanvas: offscreen 2D context unavailable');

  const RIVER_ID = 9;
  const LAKE_ID = 7;
  const SEA_ID = 8;
  const W = map.width;
  const H = map.height;
  const terrain = map.terrain;
  const isWater = (id: number): boolean => id === LAKE_ID || id === SEA_ID;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'medium';

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const idx = y * W + x;
      if (terrain[idx] !== RIVER_ID) continue;

      let bitmask = 0;
      let seaAdj = false;
      if (y > 0) {
        const n = terrain[idx - W]!;
        if (n === RIVER_ID) bitmask |= ARM_N;
        else if (isWater(n)) seaAdj = true;
      }
      if (x + 1 < W) {
        const n = terrain[idx + 1]!;
        if (n === RIVER_ID) bitmask |= ARM_E;
        else if (isWater(n)) seaAdj = true;
      }
      if (y + 1 < H) {
        const n = terrain[idx + W]!;
        if (n === RIVER_ID) bitmask |= ARM_S;
        else if (isWater(n)) seaAdj = true;
      }
      if (x > 0) {
        const n = terrain[idx - 1]!;
        if (n === RIVER_ID) bitmask |= ARM_W;
        else if (isWater(n)) seaAdj = true;
      }

      const tile = atlas.pickTile(bitmask, seaAdj);
      if (!tile) continue;

      const px = Math.floor(x * z);
      const py = Math.floor(y * z);
      const pw = Math.ceil(z);
      const ph = Math.ceil(z);
      ctx.drawImage(tile, px, py, pw, ph);
    }
  }
  return off;
}

interface TintEntry {
  readonly color: readonly [number, number, number];
  readonly alpha: number;
}

/**
 * Precompute province-id → tint entry using the current GameState +
 * player-faction id. Skips provinces not owned in the game state
 * (wilderness stays uncolored). Faction color comes from
 * `getFactionById` — the canonical §6.1 #21 palette.
 */
function buildProvinceTintLookup(
  renderState: RenderState,
  neutralColor: readonly [number, number, number],
): Map<number, TintEntry> {
  const { state, playerFactionId } = renderState;
  const PLAYER_ALPHA = 0.42;
  const AI_ALPHA = 0.30;
  const NEUTRAL_ALPHA = 0.16;
  const lookup = new Map<number, TintEntry>();
  for (const pid of sortedIds(state.provinces)) {
    const p = state.provinces.get(pid)!;
    if (p.ownerFactionId === null) continue;
    const faction = state.factions.get(p.ownerFactionId);
    if (!faction) continue;
    const isNeutral = faction.teamId === NEUTRAL_TEAM_ID;
    if (isNeutral) {
      lookup.set(pid as number, { color: neutralColor, alpha: NEUTRAL_ALPHA });
      continue;
    }
    const def = getFactionById(faction.id as unknown as number);
    const isPlayer = playerFactionId !== null && faction.id === playerFactionId;
    lookup.set(pid as number, {
      color: def.color,
      alpha: isPlayer ? PLAYER_ALPHA : AI_ALPHA,
    });
  }
  return lookup;
}

// ---------------------------------------------------------------------------
// Per-frame draw
// ---------------------------------------------------------------------------

export function drawFrame(
  ctx: CanvasRenderingContext2D,
  map: LoadedMap,
  camera: Camera,
  baked: BakedLayers,
  renderState: RenderState | null = null,
): void {
  const canvasW = ctx.canvas.width;
  const canvasH = ctx.canvas.height;

  // Background — the water-edge void beyond the map
  ctx.fillStyle = '#0e0b06';
  ctx.fillRect(0, 0, canvasW, canvasH);

  // Position all baked layers at world(0, 0).
  const origin = worldToScreen(camera, canvasW, canvasH, 0, 0);
  ctx.imageSmoothingEnabled = false;
  const ox = Math.round(origin.x);
  const oy = Math.round(origin.y);
  ctx.drawImage(baked.terrain, ox, oy);
  ctx.drawImage(baked.political, ox, oy);
  ctx.drawImage(baked.borders, ox, oy);

  // v0M14.3 (2026-07-29) — Tile-based river layer prefers the baked
  // atlas canvas. Falls back to the legacy MFix10.3 spline overlay when
  // the river atlas failed to load (e.g. missing PNGs).
  if (baked.rivers) {
    ctx.drawImage(baked.rivers, ox, oy);
  } else {
    drawRivers(ctx, map, camera, canvasW, canvasH);
  }

  // Per-frame layers
  drawCitySites(ctx, map, camera, canvasW, canvasH, renderState);

  // v0M5.2 (2026-07-25) — Fog-of-war layer per MAP.md §7.1. MFix8
  // (2026-07-25): draw fog BEFORE entity glyphs so own armies / battle
  // icons / siege icons render on top of the fog and are never dimmed
  // by it. Enemy entities are still gated by the visibility state
  // filter inside each draw function (they don't render at all in fog).
  // Reveal-all dev flag short-circuits the fog pass entirely.
  if (renderState) {
    drawFog(ctx, renderState.state, map, camera, canvasW, canvasH, renderState.playerFactionId);
  }

  if (renderState) {
    // v0M4.2.2: draw siege indicator rings over besieged cities before
    // armies + battle icons so army/battle glyphs sit on top for
    // legibility. Cross-refs `state.settlements[*].siegeState` (sieges.ts).
    drawSiegeIndicators(ctx, renderState, camera, canvasW, canvasH);
    drawPlannedPathPreview(ctx, renderState, camera, canvasW, canvasH);
    // MFix1.6: ZoC circles under the army shields so the shield reads
    // clearly on top. Skipped in the pre-menu smoke path (no state).
    drawZoCCircles(ctx, renderState, camera, canvasW, canvasH);
    // MFix5+fix2 (2026-07-23): city ZoC squares. Chebyshev-distance
    // outline around every hostile-to-someone city so the player can
    // see where the siege-trigger boundary is. Rendered same z-layer
    // as army ZoC (under the shield / icon).
    drawCityZoC(ctx, renderState, camera, canvasW, canvasH);
    // v0M7.5 (2026-07-25) — World buildables per MAP.md §9. Drawn
    // between city ZoC and armies so armies stand ON TOP of the
    // structure they're parked on (Fort defender / Watch Tower
    // contester / Bridge crosser). Renderer uses per-kind coloured
    // glyphs at v0M7 — sourced sprites can replace them in a follow-up.
    drawWorldStructures(ctx, renderState, camera, canvasW, canvasH);
    drawArmies(ctx, renderState, camera, canvasW, canvasH);
    // v0M5.3 (2026-07-25) — Spies. Drawn after armies so the tiny spy
    // icon overlays if a spy sits on the same cell as an army (own
    // spy visible; enemy spy only if the cell is state-4 detected).
    drawSpies(ctx, renderState, camera, canvasW, canvasH);
    // v0M5.4c (2026-07-25) — indicator ring on settlements where the
    // player has an own spy currently inside. Small gold circle above
    // the city sprite, distinguishes from siege pulses (red) and
    // battle icons (crossed swords).
    drawSpyInsideIndicators(ctx, renderState, camera, canvasW, canvasH);
    drawBattleIcons(ctx, renderState, camera, canvasW, canvasH);
    // MFix5.3 (2026-07-22): crossed-swords siege icon over besieged
    // cities so the siege state reads immediately, matching the visual
    // grammar of army-vs-army battle icons. Rendered after
    // drawBattleIcons so a city with BOTH a siege AND a field battle
    // still shows the field-battle icon in the correct z-order.
    drawSiegeIcons(ctx, renderState, camera, canvasW, canvasH);
    // v0M7.5b (2026-07-26) — Road polyline picker preview. Drawn
    // last (over armies + icons) so waypoint numbers stay legible.
    // No-op when not in pick mode.
    drawRoadPolylinePreview(ctx, renderState, camera, canvasW, canvasH);
  } else {
    drawPlaceholderArmy(ctx, map, camera, canvasW, canvasH);
  }

  // HUD
  drawMapLabel(ctx, map, camera, canvasW, canvasH);
  drawLegend(ctx, canvasW, canvasH);
  drawZoomIndicator(ctx, camera, canvasW, canvasH);
  drawCompass(ctx, canvasW, canvasH);
}

// ---------------------------------------------------------------------------
// Entity layer
// ---------------------------------------------------------------------------

function drawCitySites(
  ctx: CanvasRenderingContext2D,
  map: LoadedMap,
  camera: Camera,
  canvasW: number,
  canvasH: number,
  renderState: RenderState | null,
): void {
  const majorRadius = cityIconRadius(camera);
  const minorRadius = Math.max(3, Math.round(majorRadius * 0.55));
  ctx.save();
  ctx.font = '11px "SF Mono", Menlo, Consolas, monospace';

  // v0M12.4 (2026-07-26) — walled-city detection. Look up each map
  // province's dynamic state and check for the `Settlement Walls`
  // structure. Cities in provinces with walls built get a crenellated
  // outer ring per `drawCityIcon(walled=true)`. Renders per frame from
  // live state so building/losing walls updates the icon immediately.
  const walledProvinceIds = new Set<number>();
  if (renderState) {
    for (const [pid, prov] of renderState.state.provinces) {
      const hasWalls = prov.structures.some((s) => s.name === 'Settlement Walls');
      if (hasWalls) walledProvinceIds.add(pid as unknown as number);
    }
  }

  // v0M9 (2026-07-27) — build cell→settlement lookup for minor pillage/repair
  // state overlay. Cheap: one Map<int, Settlement> per frame, sized by
  // number of minors (~200). Consulted during Pass 1 below to add the
  // burning-flame + timer-bar overlays when the sim state indicates a
  // pillage or repair is in flight, or the minor is already pillaged.
  const cellKey = (x: number, y: number): number => y * 100000 + x;
  const minorSimByCell = new Map<number, import('../../sim/state').Settlement>();
  if (renderState) {
    for (const s of renderState.state.settlements.values()) {
      if (!s.isMinor) continue;
      minorSimByCell.set(cellKey(s.cell.x, s.cell.y), s);
    }
  }

  // Pass 1 — minor cities under labels (drawn first so major overlaps them if close).
  for (const province of map.provinces) {
    const minors = province.minorCities ?? [];
    for (const minor of minors) {
      const [mx, my] = minor.cell;
      const s = worldToScreen(camera, canvasW, canvasH, mx + 0.5, my + 0.5);
      if (s.x < -30 || s.x > canvasW + 30 || s.y < -30 || s.y > canvasH + 30) continue;
      drawMinorCityIcon(ctx, s.x, s.y, minorRadius, provinceTint(province.id));
      // v0M9 — pillage/repair overlays.
      const sim = minorSimByCell.get(cellKey(mx, my));
      if (sim) {
        drawMinorStatusOverlay(ctx, s.x, s.y, minorRadius, sim);
      }
    }
  }

  // Pass 2 — major cities + labels
  for (const province of map.provinces) {
    const [cx, cy] = province.citySite;
    const s = worldToScreen(camera, canvasW, canvasH, cx + 0.5, cy + 0.5);
    if (s.x < -50 || s.x > canvasW + 50 || s.y < -50 || s.y > canvasH + 50) continue;

    const wallColor = rgbToCss(provinceTint(province.id));
    drawCityIcon(ctx, {
      x: s.x,
      y: s.y,
      wallColor,
      keepColor: '#c9a24e',
      radius: majorRadius,
      walled: walledProvinceIds.has(province.id),
    });

    // Name label - with a subtle text shadow for legibility over terrain.
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(14, 11, 6, 0.7)';
    ctx.fillText(province.name, s.x + majorRadius + 5, s.y + 1);
    ctx.fillStyle = '#f0e6ce';
    ctx.fillText(province.name, s.x + majorRadius + 4, s.y);
  }
  ctx.restore();
}

/**
 * v0M9 (2026-07-27) — Overlay pillage / repair status on a minor
 * settlement icon. Draws over the existing minor icon (drawMinorCityIcon
 * has already fired for this cell).
 *
 * Visual rules (kept minimal + strategic-map-legible):
 *   - `pillaged` true: burning flame glyph over the icon + red tint.
 *   - `pillageState` non-null: red progress bar above the icon (fill %
 *     = daysElapsed / PILLAGE_DAYS).
 *   - `repairState` non-null: green progress bar above the icon
 *     (fill % = daysElapsed / REPAIR_DAYS).
 *
 * Uses the state-side dial fallbacks for the timer denominators; a
 * discrepancy from the xlsx-authored dial is cosmetic (bar % is
 * approximate; the sim owns the actual completion tick).
 */
function drawMinorStatusOverlay(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  settlement: import('../../sim/state').Settlement,
): void {
  const pillageDaysDenom = 30; // Mirrors MINOR_SETTLEMENT_PILLAGE_DAYS.
  const repairDaysDenom = 5;   // Mirrors MINOR_SETTLEMENT_REPAIR_DAYS.

  ctx.save();

  // Flame glyph — small orange/yellow teardrop above the icon when pillaged.
  if (settlement.pillaged === true) {
    // Red halo around the icon.
    ctx.strokeStyle = 'rgba(220, 60, 40, 0.85)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x, y, radius + 1.5, 0, Math.PI * 2);
    ctx.stroke();

    // Flame silhouette.
    const fx = x;
    const fy = y - radius - 2;
    const fw = Math.max(3, radius * 0.9);
    const fh = Math.max(5, radius * 1.4);
    // Outer flame body.
    ctx.fillStyle = 'rgba(220, 70, 30, 0.95)';
    ctx.beginPath();
    ctx.moveTo(fx, fy - fh);
    ctx.quadraticCurveTo(fx - fw, fy - fh * 0.4, fx - fw * 0.6, fy);
    ctx.quadraticCurveTo(fx, fy - fh * 0.15, fx + fw * 0.6, fy);
    ctx.quadraticCurveTo(fx + fw, fy - fh * 0.4, fx, fy - fh);
    ctx.closePath();
    ctx.fill();
    // Inner hot core.
    ctx.fillStyle = 'rgba(255, 220, 120, 0.9)';
    ctx.beginPath();
    ctx.moveTo(fx, fy - fh * 0.7);
    ctx.quadraticCurveTo(fx - fw * 0.4, fy - fh * 0.25, fx, fy - fh * 0.1);
    ctx.quadraticCurveTo(fx + fw * 0.4, fy - fh * 0.25, fx, fy - fh * 0.7);
    ctx.closePath();
    ctx.fill();
  }

  // Progress bar — pillage OR repair (mutually exclusive per sim).
  const pillageState = settlement.pillageState ?? null;
  const repairState = settlement.repairState ?? null;
  if (pillageState != null || repairState != null) {
    const barW = Math.max(14, radius * 3);
    const barH = 3;
    const bx = x - barW / 2;
    const by = y + radius + 3;
    // Background trough.
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(bx - 1, by - 1, barW + 2, barH + 2);
    let pct = 0;
    let color = '#7ac490';
    if (pillageState != null) {
      pct = Math.max(0, Math.min(1, pillageState.daysElapsed / pillageDaysDenom));
      color = '#d97070';
    } else if (repairState != null) {
      pct = Math.max(0, Math.min(1, repairState.daysElapsed / repairDaysDenom));
      color = '#7ac490';
    }
    ctx.fillStyle = color;
    ctx.fillRect(bx, by, barW * pct, barH);
  }

  ctx.restore();
}

/**
 * Small filled dot with an outer ring for a minor city. Distinguishable
 * from the walled-circle major-city icon at a glance.
 */
function drawMinorCityIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  tint: readonly [number, number, number],
): void {
  ctx.save();
  // Outer ring in tint colour
  ctx.strokeStyle = `rgb(${tint[0]}, ${tint[1]}, ${tint[2]})`;
  ctx.lineWidth = 1.25;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();
  // Inner filled dot in muted parchment
  ctx.fillStyle = '#e6dcc4';
  ctx.beginPath();
  ctx.arc(x, y, Math.max(1, radius * 0.55), 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * A single placeholder army near Karthholm — a small screen-scaled
 * triangle. Used in the pre-menu smoke path when no GameState exists.
 */
function drawPlaceholderArmy(
  ctx: CanvasRenderingContext2D,
  map: LoadedMap,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const karthholm = map.provinces.find((p) => p.name === 'Karthholm');
  if (!karthholm) return;
  const wx = karthholm.citySite[0] + 4;
  const wy = karthholm.citySite[1] + 2;
  const s = worldToScreen(camera, canvasW, canvasH, wx, wy);

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(s.x, s.y - 7);
  ctx.lineTo(s.x - 6, s.y + 5);
  ctx.lineTo(s.x + 6, s.y + 5);
  ctx.closePath();
  ctx.fillStyle = '#a94438';
  ctx.strokeStyle = '#1a140a';
  ctx.lineWidth = 1.25;
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

/**
 * MFix1.6 (2026-07-16) — translucent Zone-of-Control circle around every
 * Army. Radius = `zoc_radius` world cells (matches zoc-detection.ts).
 * Tint = owning faction's colour at low alpha. Armies engaged in a
 * battle skip their circle (visual clutter — the battle icon is enough).
 *
 * Rendered underneath the army shield so the shield stays legible.
 */
function drawZoCCircles(
  ctx: CanvasRenderingContext2D,
  renderState: RenderState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const { state, playerFactionId } = renderState;
  const z = pxPerCell(camera);
  const radiusPx = ZOC_RADIUS_CELLS * z;
  // Cull threshold — if the whole circle is well off-screen, skip.
  const cull = radiusPx + 30;

  ctx.save();
  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId)!;
    if (army.currentBattleId !== null) continue;
    const faction = state.factions.get(army.ownerFactionId);
    if (!faction) continue;

    // MFix8 (2026-07-25) — after moving to post-fog draw, filter enemy
    // ZoC circles by visibility. Own ZoC always visible; enemy ZoC
    // hidden unless the army's cell is state ≥ 3 for the player.
    const isOwnArmy = playerFactionId !== null && army.ownerFactionId === playerFactionId;
    if (!isOwnArmy) {
      if (!isCellVisibleToPlayer(state, playerFactionId, army.position.x, army.position.y)) continue;
    }

    const s = worldToScreen(camera, canvasW, canvasH, army.position.x, army.position.y);
    if (s.x < -cull || s.x > canvasW + cull || s.y < -cull || s.y > canvasH + cull) continue;

    const isNeutral = faction.teamId === NEUTRAL_TEAM_ID;
    let color: readonly [number, number, number];
    if (isNeutral) {
      color = [140, 130, 110];
    } else {
      const def = getFactionById(faction.id as unknown as number);
      color = def.color;
    }
    const cssColor = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;

    // MFix14 (2026-07-29) — Higher-contrast ZoC ring so it reads against
    // the province tint (which is often the same faction colour). Fill
    // alpha bumped 0.12 → 0.18; ring gets a dark shadow underlay + a
    // brighter faction-colour stroke on top for double-outline pop.
    ctx.beginPath();
    ctx.arc(s.x, s.y, radiusPx, 0, Math.PI * 2);
    ctx.fillStyle = cssColor;
    ctx.globalAlpha = 0.18;
    ctx.fill();
    // Dark contrast underlay — makes the ring visible on same-colour
    // province tint.
    ctx.globalAlpha = 0.55;
    ctx.strokeStyle = '#1a140a';
    ctx.lineWidth = 3;
    ctx.stroke();
    // Faction-colour ring on top, slightly thinner.
    ctx.globalAlpha = 0.95;
    ctx.strokeStyle = cssColor;
    ctx.lineWidth = 1.75;
    ctx.stroke();
    ctx.globalAlpha = 1.0;
  }
  ctx.restore();
}

/**
 * v0M4.2.2 — pulse a red ring around each besieged city so the player
 * can see at a glance which of their cities are under starve-out.
 * Ring pulse driven by wall-clock time, so it renders smoothly across
 * paused-sim states (the sim is what carries siege state; the client
 * just visualises it).
 *
 * Cross-refs: `state.settlements[*].siegeState` set by `sieges.ts`.
 */
function drawSiegeIndicators(
  ctx: CanvasRenderingContext2D,
  renderState: RenderState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const { state, playerFactionId } = renderState;
  const z = pxPerCell(camera);
  const majorRadius = cityIconRadius(camera);
  // Pulse: sine wave in [0.6, 1.0] alpha over a 2-second cycle.
  const t = performance.now() / 1000;
  const alpha = 0.6 + 0.4 * (0.5 + 0.5 * Math.sin(t * Math.PI));
  const ringRadius = majorRadius + Math.max(4, Math.round(z * 0.5));

  ctx.save();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = `rgba(220, 60, 60, ${alpha})`;
  for (const settlementId of sortedIds(state.settlements)) {
    const city = state.settlements.get(settlementId);
    if (!city) continue;
    if (city.siegeState === null) continue;

    // MFix8 (2026-07-25) — filter siege pulse rings by player visibility.
    // Own city or own sieging army always shown; other besieged cities
    // hidden unless the city cell is state ≥ 3 for the player.
    const cityProvince = state.provinces.get(city.provinceId);
    const isOwnCity = playerFactionId !== null && cityProvince?.ownerFactionId === playerFactionId;
    const siegerArmy = state.armies.get(city.siegeState.siegingArmyId);
    const isOwnSieger = playerFactionId !== null && siegerArmy?.ownerFactionId === playerFactionId;
    if (!isOwnCity && !isOwnSieger && !isCellVisibleToPlayer(state, playerFactionId, city.cell.x, city.cell.y)) continue;

    const s = worldToScreen(camera, canvasW, canvasH, city.cell.x + 0.5, city.cell.y + 0.5);
    if (s.x < -30 || s.x > canvasW + 30 || s.y < -30 || s.y > canvasH + 30) continue;
    ctx.beginPath();
    ctx.arc(s.x, s.y, ringRadius, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Draw every Army in `state.armies` at its float world position, coloured
 * by its owning faction's canonical color. The player's army gets a
 * brighter parchment outline + slightly larger radius so it stands out
 * at a glance. Zoom-scaled radius so armies don't dominate at max zoom
 * or disappear at min zoom.
 */
function drawArmies(
  ctx: CanvasRenderingContext2D,
  renderState: RenderState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const { state, playerFactionId } = renderState;
  const z = pxPerCell(camera);
  // Radius grows with zoom but has floors + ceilings so armies read at
  // every zoom step. At z=2 (min) radius ≈ 6; at z=8 (max) radius ≈ 11.
  const baseRadius = Math.max(6, Math.min(11, Math.round(z * 1.4)));

  ctx.save();
  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId)!;
    const s = worldToScreen(camera, canvasW, canvasH, army.position.x, army.position.y);
    if (s.x < -30 || s.x > canvasW + 30 || s.y < -30 || s.y > canvasH + 30) continue;

    // v0M5.2 (2026-07-25) — filter enemy armies by player visibility.
    // Own armies always drawn (they don't hide from you). Enemy armies
    // require state ≥ 3 (Visible) at their current cell. The fog layer
    // handles cosmetic dimming; this filter prevents enemies peeking
    // through the state-2 wash.
    const isOwnArmy = playerFactionId !== null && army.ownerFactionId === playerFactionId;
    if (!isOwnArmy) {
      if (!isCellVisibleToPlayer(state, playerFactionId, army.position.x, army.position.y)) continue;
    }

    const faction = state.factions.get(army.ownerFactionId);
    if (!faction) continue;
    const isNeutral = faction.teamId === NEUTRAL_TEAM_ID;
    // Neutrals shouldn't have armies at v0M3.7a — but if a future feature
    // creates one, fall back to a muted grey rather than the canonical
    // faction color.
    let color: readonly [number, number, number];
    if (isNeutral) {
      color = [140, 130, 110];
    } else {
      const def = getFactionById(faction.id as unknown as number);
      color = def.color;
    }
    const isPlayer = playerFactionId !== null && army.ownerFactionId === playerFactionId;
    const isSelected = renderState.selectedArmyId != null && army.id === renderState.selectedArmyId;
    const radius = isSelected ? baseRadius + 2 : isPlayer ? baseRadius + 1 : baseRadius;

    // v0M12.5 (2026-07-26) — sprite-first path. Faction-colour disc
    // painted under the sprite; the sprite itself is tinted with the
    // faction colour so armies of different factions read distinctly.
    // MFix14+fix (2026-07-29) — was drawn as raw drawImage which
    // rendered the sprite's original colour (white eagle on transparent
    // bg after the SVG-cleanup, so every army looked identical). Now
    // tinted per-army via `renderTintedSprite` (offscreen canvas +
    // source-in composite; see the helper for details).
    const armySprite = ensureArmyIconLoaded();
    if (armySprite !== null) {
      // Coloured disc UNDER the sprite for faction tint. Darkened
      // slightly so the tinted eagle silhouette on top reads with
      // contrast against the same base colour.
      const discColor = [
        Math.round(color[0] * 0.55),
        Math.round(color[1] * 0.55),
        Math.round(color[2] * 0.55),
      ] as const;
      ctx.beginPath();
      ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = `rgb(${discColor[0]}, ${discColor[1]}, ${discColor[2]})`;
      ctx.fill();
      // Outline.
      if (isSelected) {
        ctx.strokeStyle = '#ffdc84';
        ctx.lineWidth = 3;
      } else {
        ctx.strokeStyle = isPlayer ? '#f5eacf' : '#1a140a';
        ctx.lineWidth = isPlayer ? 2 : 1.25;
      }
      ctx.stroke();
      // Sprite (silhouette) on top, tinted with the faction colour.
      // MFix14 (2026-07-29) — sprite scale bumped from 1.6 → 2.4 × radius
      // (+50%) per Michael's ask so the eagle actually reads at strategic
      // zoom.
      const spriteSize = radius * 2.4;
      renderTintedSprite(
        ctx,
        armySprite,
        s.x - spriteSize / 2,
        s.y - spriteSize / 2,
        spriteSize,
        color,
      );
    } else {
      // Canvas-shield fallback (v0M3.7a original). Fires when the SVG
      // hasn't loaded yet OR is missing on disk.
      ctx.beginPath();
      ctx.moveTo(s.x - radius, s.y - radius);
      ctx.lineTo(s.x + radius, s.y - radius);
      ctx.lineTo(s.x + radius, s.y + radius * 0.5);
      ctx.lineTo(s.x, s.y + radius);
      ctx.lineTo(s.x - radius, s.y + radius * 0.5);
      ctx.closePath();
      ctx.fillStyle = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
      ctx.fill();
      if (isSelected) {
        ctx.strokeStyle = '#ffdc84';
        ctx.lineWidth = 3;
      } else {
        ctx.strokeStyle = isPlayer ? '#f5eacf' : '#1a140a';
        ctx.lineWidth = isPlayer ? 2 : 1.25;
      }
      ctx.stroke();
    }

    // Cohort count pip — tiny number in the shield centre. Skip at
    // smallest zoom to avoid clutter.
    if (z >= 4) {
      ctx.fillStyle = '#f5eacf';
      ctx.strokeStyle = '#1a140a';
      ctx.lineWidth = 2.5;
      ctx.font = `bold ${Math.max(9, radius - 1)}px "SF Mono", Menlo, Consolas, monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const label = String(army.cohortIds.length);
      ctx.strokeText(label, s.x, s.y - 1);
      ctx.fillText(label, s.x, s.y - 1);
    }
  }
  ctx.restore();
}

/**
 * Draw a crossed-swords icon at the midpoint of each active battle's
 * participating armies (v0M3.5). Clickable in `hit-test.ts` to open the
 * battle-command modal. Player-participation is NOT filtered here —
 * players can observe any battle they can see (but only click into
 * their own via hit-test's ownership check).
 */
/**
 * MFix3.7 — Preloaded battle-icon sprite. Loaded lazy on first render
 * request; the pre-load canvas-fallback triggers if the sprite hasn't
 * arrived yet or fails to load (e.g. file missing). One-time cost per
 * page load; the browser cache holds the SVG after that.
 */
let battleIconSprite: HTMLImageElement | null = null;
let battleIconLoadStarted = false;
function ensureBattleIconLoaded(): HTMLImageElement | null {
  if (battleIconLoadStarted) return battleIconSprite;
  battleIconLoadStarted = true;
  const img = new Image();
  img.onload = () => { battleIconSprite = img; };
  img.onerror = () => { battleIconSprite = null; };
  img.src = '/icons/battle/crossed-swords.svg';
  return null;
}

/**
 * v0M12.5 (2026-07-26) — Army icon sprite cache. Same pattern as
 * `ensureBattleIconLoaded`. Load target:
 *   `/assets/units/army-standard.svg`
 * Michael-owed asset (candidates from `public/icons/`:
 * `eagle-emblem.svg`, `spartan-helmet.svg`, `centurion-helmet.svg`,
 * `crested-helmet.svg`, `galea.svg`, `gladius.svg`, `laurel-crown.svg`,
 * `chariot.svg`). To use one of those, copy it to
 * `public/assets/units/army-standard.svg`. Missing → fall back to the
 * v0M3.7a shield-shape canvas draw.
 */
let armyIconSprite: HTMLImageElement | null = null;
let armyIconLoadStarted = false;
function ensureArmyIconLoaded(): HTMLImageElement | null {
  if (armyIconLoadStarted) return armyIconSprite;
  armyIconLoadStarted = true;
  const img = new Image();
  img.onload = () => { armyIconSprite = img; };
  img.onerror = () => { armyIconSprite = null; };
  // MFix14 (2026-07-29) — swapped from army-standard.svg → eagle-emblem.svg
  // per Michael's ask; both live in /assets/units/. Also see the 50%-larger
  // sprite scale in drawArmies (1.6 → 2.4) so the eagle actually reads at
  // strategic zoom. Companion SVG-cleanup pass stripped the black backdrop
  // rect so drawImage no longer paints a solid black square around the
  // eagle silhouette — same fix the person.svg cell-figure got at
  // session-28b (see CLAUDE.md gotcha).
  img.src = '/assets/units/eagle-emblem.svg';
  return null;
}

/**
 * MFix14+fix (2026-07-29) — Draw `sprite` onto `ctx` tinted with a
 * faction colour, via an offscreen canvas that we reuse across armies.
 *
 * Pipeline:
 *   1. Resize the reusable offscreen canvas to the requested size + clear it.
 *   2. Draw the source sprite at target size — after the SVG cleanup its
 *      opaque region is just the eagle silhouette.
 *   3. `globalCompositeOperation = 'source-in'` + fill with the faction
 *      colour → recolours ONLY the eagle silhouette (transparent pixels
 *      stay transparent).
 *   4. Reset composite mode + blit the tinted offscreen onto the main
 *      canvas at the requested (dx, dy).
 *
 * Why this exists: the previous raw `drawImage` painted the sprite in
 * its authored colour (white), which made every army look identical
 * regardless of owner (Michael's playtest report). Tinting the
 * silhouette per faction restores per-owner visual identity.
 *
 * Perf: one offscreen canvas reused across all armies each frame.
 * `spriteSize` is small (~15-25 px at typical zoom) so the fill +
 * composite is cheap. If the frame-time budget bites, cache per
 * (color, size) at the cost of more state.
 */
let tintOffscreen: HTMLCanvasElement | null = null;
function renderTintedSprite(
  ctx: CanvasRenderingContext2D,
  sprite: HTMLImageElement,
  dx: number,
  dy: number,
  size: number,
  color: readonly [number, number, number],
): void {
  const w = Math.max(1, Math.ceil(size));
  const h = Math.max(1, Math.ceil(size));
  if (tintOffscreen === null) tintOffscreen = document.createElement('canvas');
  if (tintOffscreen.width !== w || tintOffscreen.height !== h) {
    tintOffscreen.width = w;
    tintOffscreen.height = h;
  }
  const tctx = tintOffscreen.getContext('2d');
  if (!tctx) {
    // Fallback: draw the raw sprite (untinted) rather than nothing.
    ctx.drawImage(sprite, dx, dy, size, size);
    return;
  }
  tctx.clearRect(0, 0, w, h);
  tctx.globalCompositeOperation = 'source-over';
  tctx.drawImage(sprite, 0, 0, w, h);
  tctx.globalCompositeOperation = 'source-in';
  tctx.fillStyle = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
  tctx.fillRect(0, 0, w, h);
  tctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(tintOffscreen, dx, dy, size, size);
}

function drawBattleIcons(
  ctx: CanvasRenderingContext2D,
  renderState: RenderState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const { state, playerFactionId } = renderState;
  const sprite = ensureBattleIconLoaded();
  ctx.save();
  for (const battleId of sortedIds(state.battles)) {
    const battle = state.battles.get(battleId)!;
    if (battle.result !== null) continue;
    const [aId, bId] = battle.armyIds;
    const armyA = state.armies.get(aId);
    const armyB = state.armies.get(bId);
    if (!armyA || !armyB) continue;
    // MFix6.1 (2026-07-23): use the shared helper so the icon shifts
    // off any city cell (city stays clickable) and hit-test agrees.
    const iconPos = computeBattleIconWorldPos(state, armyA, armyB);
    const s = worldToScreen(camera, canvasW, canvasH, iconPos.x, iconPos.y);
    if (s.x < -30 || s.x > canvasW + 30 || s.y < -30 || s.y > canvasH + 30) continue;

    // v0M5.2 (2026-07-25) — battle icon visibility. Show if the player is
    // participating (one of their own armies is in the battle) OR the
    // icon's cell is state ≥ 3 for them. A battle two enemies fight in
    // fog stays hidden until vision reaches it.
    const isPlayerInBattle =
      playerFactionId !== null &&
      (armyA.ownerFactionId === playerFactionId || armyB.ownerFactionId === playerFactionId);
    if (!isPlayerInBattle && !isCellVisibleToPlayer(state, playerFactionId, iconPos.x, iconPos.y)) continue;

    // MFix3.7: sourced sprite (crossed-swords) with a darker dropshadow
    // for legibility. Falls back to the pre-MFix3.7 canvas draw while
    // the sprite is still loading or if it failed to load.
    const R = 16;
    if (sprite !== null) {
      // Dropshadow: draw the sprite at a slight offset in near-black.
      ctx.save();
      ctx.filter = 'brightness(0)';
      ctx.globalAlpha = 0.6;
      ctx.drawImage(sprite, s.x - R + 1.5, s.y - R + 1.5, R * 2, R * 2);
      ctx.restore();
      // Main icon.
      ctx.drawImage(sprite, s.x - R, s.y - R, R * 2, R * 2);
    } else {
      // Fallback: canvas-drawn crossed-swords (pre-MFix3.7 path).
      ctx.strokeStyle = '#1a140a';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(s.x - R, s.y - R);
      ctx.lineTo(s.x + R, s.y + R);
      ctx.moveTo(s.x - R, s.y + R);
      ctx.lineTo(s.x + R, s.y - R);
      ctx.stroke();
      ctx.strokeStyle = '#f4d47a';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(s.x - R, s.y - R);
      ctx.lineTo(s.x + R, s.y + R);
      ctx.moveTo(s.x - R, s.y + R);
      ctx.lineTo(s.x + R, s.y - R);
      ctx.stroke();
    }

    // Round counter — small label below the icon so Michael can tell
    // whether AI-vs-AI battles are progressing or stuck (v0M3.5+fix).
    ctx.font = 'bold 11px "SF Mono", Menlo, Consolas, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const label = `R${battle.round}`;
    ctx.strokeStyle = '#1a140a';
    ctx.lineWidth = 3;
    ctx.strokeText(label, s.x, s.y + R + 8);
    ctx.fillStyle = '#f5eacf';
    ctx.fillText(label, s.x, s.y + R + 8);
  }
  ctx.restore();
}

/**
 * Settlement ZoC visual. MFix5+fix2 (2026-07-23) shipped this as a
 * Chebyshev square outline; **MFix14 (2026-07-29) swapped to a circle**
 * per Michael's ask that settlement ZoCs match army ZoCs in shape (and
 * `sieges.ts` was also switched to Euclidean adjacency for the same
 * reason). Radius = `settlement_zoc_radius + 0.5` = the actual
 * SIEGE_ADJACENCY_RADIUS reach.
 *
 * Tinted by the city-owner faction (grey/neutral, faction colour).
 * Higher-contrast dark-underlay + faction-colour double stroke so the
 * ring reads against same-colour province tint (also part of MFix14).
 */
function drawCityZoC(
  ctx: CanvasRenderingContext2D,
  renderState: RenderState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const { state, playerFactionId } = renderState;
  const z = pxPerCell(camera);
  // Circle radius in world cells, including the +0.5 slop from
  // sieges.ts (SIEGE_ADJACENCY_RADIUS = SETTLEMENT_ZOC_RADIUS + 0.5).
  // The +0.5 makes the visual match the actual trigger reach.
  const radiusCells = CITY_ZOC_RADIUS_CELLS + 0.5;
  const radiusPx = radiusCells * z;
  const cull = radiusPx + 30;

  ctx.save();
  for (const settlementId of sortedIds(state.settlements)) {
    const city = state.settlements.get(settlementId);
    if (!city) continue;
    const province = state.provinces.get(city.provinceId);
    if (!province) continue;

    // MFix8 (2026-07-25) — filter city ZoC by player visibility.
    // Own cities always show; other cities hidden unless the city cell is
    // state ≥ 3 for the player.
    const isOwnCity = playerFactionId !== null && province.ownerFactionId === playerFactionId;
    if (!isOwnCity) {
      if (!isCellVisibleToPlayer(state, playerFactionId, city.cell.x, city.cell.y)) continue;
    }

    // Screen position of the city cell centre.
    const centre = worldToScreen(camera, canvasW, canvasH, city.cell.x + 0.5, city.cell.y + 0.5);
    if (centre.x < -cull || centre.x > canvasW + cull || centre.y < -cull || centre.y > canvasH + cull) continue;

    // Owner colour. Null owner (neutral) → grey.
    let color: readonly [number, number, number];
    const ownerFactionId = province.ownerFactionId;
    if (ownerFactionId === null) {
      color = [140, 130, 110];
    } else {
      const faction = state.factions.get(ownerFactionId);
      const isNeutral = faction !== undefined && faction.teamId === NEUTRAL_TEAM_ID;
      if (isNeutral) {
        color = [140, 130, 110];
      } else {
        const def = getFactionById(ownerFactionId as unknown as number);
        color = def.color;
      }
    }
    const cssColor = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;

    // Filled disc with higher alpha than the pre-MFix14 square (0.10 →
    // 0.16) so it reads against same-colour province tint. Then a dark
    // shadow underlay + faction-colour dashed ring on top — dashed to
    // distinguish from the solid army-ZoC ring.
    ctx.beginPath();
    ctx.arc(centre.x, centre.y, radiusPx, 0, Math.PI * 2);
    ctx.fillStyle = cssColor;
    ctx.globalAlpha = 0.16;
    ctx.fill();
    // Dark contrast underlay.
    ctx.globalAlpha = 0.55;
    ctx.strokeStyle = '#1a140a';
    ctx.lineWidth = 3;
    ctx.setLineDash([]);
    ctx.stroke();
    // Faction-colour dashed ring on top.
    ctx.globalAlpha = 0.95;
    ctx.strokeStyle = cssColor;
    ctx.lineWidth = 1.75;
    ctx.setLineDash([4, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1.0;
  }
  ctx.restore();
}

/**
 * MFix5.3 (2026-07-22) — Draw a crossed-swords icon over each city
 * whose `siegeState` is active. Matches `drawBattleIcons` visual
 * grammar (sourced sprite + dropshadow + fallback canvas draw + day-
 * remaining label) so Michael has a consistent "engagement here" glyph
 * across battles + sieges. Also draws a small siege badge (siege icon
 * has a "S" prefix on the day counter label) to distinguish from
 * regular battles at a glance.
 */
function drawSiegeIcons(
  ctx: CanvasRenderingContext2D,
  renderState: RenderState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const { state, playerFactionId } = renderState;
  const sprite = ensureBattleIconLoaded();
  ctx.save();
  for (const settlementId of sortedIds(state.settlements)) {
    const city = state.settlements.get(settlementId);
    if (!city) continue;
    if (city.siegeState === null) continue;

    // v0M5.2 — siege visibility. Own city, own sieging army, or the city
    // cell is state ≥ 3 for the player. Otherwise the siege is happening
    // in fog and stays hidden until vision reaches it.
    const cityProvince = state.provinces.get(city.provinceId);
    const isOwnCity = playerFactionId !== null && cityProvince?.ownerFactionId === playerFactionId;
    const siegerArmy = state.armies.get(city.siegeState.siegingArmyId);
    const isOwnSieger = playerFactionId !== null && siegerArmy?.ownerFactionId === playerFactionId;
    if (!isOwnCity && !isOwnSieger && !isCellVisibleToPlayer(state, playerFactionId, city.cell.x, city.cell.y)) continue;

    // Position: midpoint between the city cell and the sieging army,
    // offset upward slightly so the icon sits above the city sprite.
    // Falls back to the city cell if the sieging army has gone missing.
    const sieger = state.armies.get(city.siegeState.siegingArmyId);
    let midX: number;
    let midY: number;
    if (sieger) {
      midX = (city.cell.x + sieger.position.x) / 2 + 0.5;
      midY = (city.cell.y + sieger.position.y) / 2 + 0.5;
    } else {
      midX = city.cell.x + 0.5;
      midY = city.cell.y + 0.5;
    }
    const s = worldToScreen(camera, canvasW, canvasH, midX, midY);
    if (s.x < -30 || s.x > canvasW + 30 || s.y < -30 || s.y > canvasH + 30) continue;

    const R = 16;
    if (sprite !== null) {
      // Dropshadow.
      ctx.save();
      ctx.filter = 'brightness(0)';
      ctx.globalAlpha = 0.6;
      ctx.drawImage(sprite, s.x - R + 1.5, s.y - R + 1.5, R * 2, R * 2);
      ctx.restore();
      // Main icon (slightly tinted red to signal "siege" vs "battle").
      ctx.drawImage(sprite, s.x - R, s.y - R, R * 2, R * 2);
    } else {
      // Fallback canvas draw (same as drawBattleIcons pre-MFix3.7).
      ctx.strokeStyle = '#1a140a';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(s.x - R, s.y - R);
      ctx.lineTo(s.x + R, s.y + R);
      ctx.moveTo(s.x - R, s.y + R);
      ctx.lineTo(s.x + R, s.y - R);
      ctx.stroke();
      ctx.strokeStyle = '#f4d47a';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(s.x - R, s.y - R);
      ctx.lineTo(s.x + R, s.y + R);
      ctx.moveTo(s.x - R, s.y + R);
      ctx.lineTo(s.x + R, s.y - R);
      ctx.stroke();
    }

    // Day counter — days remaining before city surrenders.
    const daysRemaining = Math.max(0, Math.ceil(city.siegeState.daysRemaining));
    ctx.font = 'bold 11px "SF Mono", Menlo, Consolas, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const label = `S ${daysRemaining}d`;
    ctx.strokeStyle = '#1a140a';
    ctx.lineWidth = 3;
    ctx.strokeText(label, s.x, s.y + R + 8);
    ctx.fillStyle = '#f5eacf';
    ctx.fillText(label, s.x, s.y + R + 8);
  }

  // v0M7.4d+siege2 (2026-07-26) — Fort-siege icons. Peer of the
  // settlement loop above: render the same crossed-swords icon over
  // any besieged Fort. Visibility gating mirrors settlements
  // (own-Fort, own-sieger, or fort cell state≥3 for player).
  if (state.worldStructures) {
    for (const wsId of sortedIds(state.worldStructures)) {
      const fort = state.worldStructures.get(wsId);
      if (!fort) continue;
      if (fort.kind !== 'fort') continue;
      if (fort.siegeState == null) continue;

      const isOwnFort = playerFactionId !== null && fort.ownerFactionId === playerFactionId;
      const siegerArmy = state.armies.get(fort.siegeState.siegingArmyId);
      const isOwnSieger = playerFactionId !== null && siegerArmy?.ownerFactionId === playerFactionId;
      if (!isOwnFort && !isOwnSieger && !isCellVisibleToPlayer(state, playerFactionId, fort.cell.x, fort.cell.y)) continue;

      const sieger = siegerArmy;
      let midX: number;
      let midY: number;
      if (sieger) {
        midX = (fort.cell.x + sieger.position.x) / 2 + 0.5;
        midY = (fort.cell.y + sieger.position.y) / 2 + 0.5;
      } else {
        midX = fort.cell.x + 0.5;
        midY = fort.cell.y + 0.5;
      }
      const s = worldToScreen(camera, canvasW, canvasH, midX, midY);
      if (s.x < -30 || s.x > canvasW + 30 || s.y < -30 || s.y > canvasH + 30) continue;

      const R = 16;
      if (sprite !== null) {
        ctx.save();
        ctx.filter = 'brightness(0)';
        ctx.globalAlpha = 0.6;
        ctx.drawImage(sprite, s.x - R + 1.5, s.y - R + 1.5, R * 2, R * 2);
        ctx.restore();
        ctx.drawImage(sprite, s.x - R, s.y - R, R * 2, R * 2);
      } else {
        ctx.strokeStyle = '#1a140a';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(s.x - R, s.y - R);
        ctx.lineTo(s.x + R, s.y + R);
        ctx.moveTo(s.x - R, s.y + R);
        ctx.lineTo(s.x + R, s.y - R);
        ctx.stroke();
        ctx.strokeStyle = '#f4d47a';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(s.x - R, s.y - R);
        ctx.lineTo(s.x + R, s.y + R);
        ctx.moveTo(s.x - R, s.y + R);
        ctx.lineTo(s.x + R, s.y - R);
        ctx.stroke();
      }

      const daysRemaining = Math.max(0, Math.ceil(fort.siegeState.daysRemaining));
      ctx.font = 'bold 11px "SF Mono", Menlo, Consolas, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const label = `S ${daysRemaining}d`;
      ctx.strokeStyle = '#1a140a';
      ctx.lineWidth = 3;
      ctx.strokeText(label, s.x, s.y + R + 8);
      ctx.fillStyle = '#f5eacf';
      ctx.fillText(label, s.x, s.y + R + 8);
    }
  }

  ctx.restore();
}

/**
 * Draw the selected army's planned path as a dotted polyline from its
 * current position through each waypoint to the movement goal. No-op if
 * no army is selected or the selected army has no plannedPath (idle).
 */
function drawPlannedPathPreview(
  ctx: CanvasRenderingContext2D,
  renderState: RenderState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const { state, selectedArmyId } = renderState;
  if (selectedArmyId == null) return;
  const army = state.armies.get(selectedArmyId);
  if (!army) return;
  const hasPath = army.plannedPath !== null && army.plannedPath.length > 0;
  const hasQueued = army.waypoints !== undefined && army.waypoints.length > 0;
  if (!hasPath && !hasQueued) return;

  ctx.save();
  ctx.strokeStyle = '#ffdc84';
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 4]);

  // Segment 1 — current plannedPath from army position through waypoints.
  if (hasPath) {
    const start = worldToScreen(camera, canvasW, canvasH, army.position.x, army.position.y);
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    for (const [wx, wy] of army.plannedPath!) {
      const s = worldToScreen(camera, canvasW, canvasH, wx + 0.5, wy + 0.5);
      ctx.lineTo(s.x, s.y);
    }
    ctx.stroke();
  }

  // Goal marker (small ring at current-goal destination).
  if (army.movementGoal !== null) {
    const g = worldToScreen(
      camera,
      canvasW,
      canvasH,
      army.movementGoal.x + 0.5,
      army.movementGoal.y + 0.5,
    );
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(g.x, g.y, 6, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([6, 4]);
  }

  // MFix12.4 (2026-07-27) — draw queued waypoints as a chain of dashed
  // segments starting from the current goal (or army position if no goal),
  // with numbered dots at each queued waypoint. Note the sim doesn't
  // pathfind these until each becomes the active goal, so the preview
  // is straight-line from goal to waypoint; the actual walk will follow
  // the pathfinder's route which may curve around obstacles.
  if (hasQueued && army.waypoints) {
    let prevWorld: { x: number; y: number };
    if (army.movementGoal !== null) {
      prevWorld = { x: army.movementGoal.x + 0.5, y: army.movementGoal.y + 0.5 };
    } else {
      prevWorld = { x: army.position.x, y: army.position.y };
    }
    for (let i = 0; i < army.waypoints.length; i++) {
      const wp = army.waypoints[i]!;
      const target = { x: wp.x + 0.5, y: wp.y + 0.5 };
      const prevS = worldToScreen(camera, canvasW, canvasH, prevWorld.x, prevWorld.y);
      const targS = worldToScreen(camera, canvasW, canvasH, target.x, target.y);
      // Dashed segment between prev and target.
      ctx.beginPath();
      ctx.moveTo(prevS.x, prevS.y);
      ctx.lineTo(targS.x, targS.y);
      ctx.stroke();
      // Numbered dot at target.
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(targS.x, targS.y, 8, 0, Math.PI * 2);
      ctx.fillStyle = '#3a2814';
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#ffdc84';
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      // Waypoint numbering: index 0 is the NEXT queued after current
      // goal, so displayed as #2 (goal = #1).
      ctx.fillText(String(i + 2), targS.x, targS.y);
      ctx.setLineDash([6, 4]);
      prevWorld = target;
    }
  }
  ctx.restore();
}

/**
 * v0M7.5b (2026-07-26) — Draw the Road-polyline picker overlay while
 * the player is picking waypoints for a Build Road order. Numbered
 * gold dots at each waypoint; dashed gold segments between adjacent
 * waypoints; small status label near waypoint 1 with the count and a
 * "RMB / Enter = commit" hint. No-op when `pendingRoadPolyline` is
 * null or empty (nothing being picked).
 */
function drawRoadPolylinePreview(
  ctx: CanvasRenderingContext2D,
  renderState: RenderState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const wpts = renderState.pendingRoadPolyline;
  if (!wpts || wpts.length === 0) return;
  ctx.save();
  // Dashed line between adjacent waypoints (cell centre to cell centre).
  if (wpts.length >= 2) {
    ctx.strokeStyle = '#ffe08a';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    const first = wpts[0]!;
    const s0 = worldToScreen(camera, canvasW, canvasH, first.x + 0.5, first.y + 0.5);
    ctx.moveTo(s0.x, s0.y);
    for (let i = 1; i < wpts.length; i++) {
      const w = wpts[i]!;
      const s = worldToScreen(camera, canvasW, canvasH, w.x + 0.5, w.y + 0.5);
      ctx.lineTo(s.x, s.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }
  // Numbered waypoint dots.
  for (let i = 0; i < wpts.length; i++) {
    const w = wpts[i]!;
    const s = worldToScreen(camera, canvasW, canvasH, w.x + 0.5, w.y + 0.5);
    ctx.fillStyle = '#3a2c14';
    ctx.strokeStyle = '#ffe08a';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(s.x, s.y, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#ffe08a';
    ctx.font = 'bold 10px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(i + 1), s.x, s.y + 0.5);
  }
  // Status hint anchored to waypoint 1 (offset above so it clears the
  // dot). Reads: "N waypoints — LMB add · RMB/Enter build · Esc cancel".
  const anchor = wpts[0]!;
  const a = worldToScreen(camera, canvasW, canvasH, anchor.x + 0.5, anchor.y + 0.5);
  const label = `${wpts.length} waypoint${wpts.length === 1 ? '' : 's'} — LMB add · RMB/Enter build · Esc cancel`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = '11px sans-serif';
  const textW = ctx.measureText(label).width;
  ctx.fillStyle = 'rgba(20, 15, 8, 0.85)';
  ctx.fillRect(a.x + 10, a.y - 22, textW + 10, 16);
  ctx.strokeStyle = 'rgba(255, 224, 138, 0.7)';
  ctx.lineWidth = 1;
  ctx.strokeRect(a.x + 10, a.y - 22, textW + 10, 16);
  ctx.fillStyle = '#ffe08a';
  ctx.fillText(label, a.x + 15, a.y - 10);
  ctx.restore();
}

/**
 * v0M5.3 (2026-07-25) — Draw all Spies on the strategic map.
 *
 * Rules:
 *   - Own spies: always drawn (visibility bypass — you always see your own).
 *   - Enemy spies: only drawn if the spy's cell is state 4 (Detection) for
 *     the player. Enemy spies with `Invisibility` (all spies at v1) never
 *     show under normal state-3 vision.
 *
 * Visual: tiny diamond icon with faction-coloured fill + parchment outline
 * for own, dark outline for others. Selected own spy gets a gold pulse.
 * Small enough (~7 px radius at mid zoom) to distinguish from army shields.
 */
function drawSpies(
  ctx: CanvasRenderingContext2D,
  renderState: RenderState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const { state, playerFactionId } = renderState;
  if (!state.spies) return;
  const z = pxPerCell(camera);
  const baseRadius = Math.max(4, Math.min(8, Math.round(z * 1.0)));

  ctx.save();
  for (const spyId of sortedIds(state.spies)) {
    const spy = state.spies.get(spyId)!;
    // v0M5.4 — hide spies that are inside settlements.
    if (spy.insideSettlementId) continue;
    const s = worldToScreen(camera, canvasW, canvasH, spy.position.x, spy.position.y);
    if (s.x < -20 || s.x > canvasW + 20 || s.y < -20 || s.y > canvasH + 20) continue;

    const isOwnSpy = playerFactionId !== null && spy.ownerFactionId === playerFactionId;
    // Detection gate for enemy spies (Invisibility → require state 4).
    if (!isOwnSpy) {
      if (!isCellDetectedByPlayer(state, playerFactionId, spy.position.x, spy.position.y)) continue;
    }

    const faction = state.factions.get(spy.ownerFactionId);
    if (!faction) continue;
    const isNeutral = faction.teamId === NEUTRAL_TEAM_ID;
    let color: readonly [number, number, number];
    if (isNeutral) {
      color = [140, 130, 110];
    } else {
      const def = getFactionById(faction.id as unknown as number);
      color = def.color;
    }
    const isSelected =
      renderState.selectedSpyId != null && spy.id === renderState.selectedSpyId;
    const radius = isSelected ? baseRadius + 2 : baseRadius;

    // Diamond marker (rotated square). Distinct from army shields.
    ctx.beginPath();
    ctx.moveTo(s.x, s.y - radius);
    ctx.lineTo(s.x + radius, s.y);
    ctx.lineTo(s.x, s.y + radius);
    ctx.lineTo(s.x - radius, s.y);
    ctx.closePath();
    ctx.fillStyle = `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
    ctx.fill();
    if (isSelected) {
      ctx.strokeStyle = '#ffdc84';
      ctx.lineWidth = 2.5;
    } else {
      ctx.strokeStyle = isOwnSpy ? '#f5eacf' : '#1a140a';
      ctx.lineWidth = isOwnSpy ? 1.75 : 1.25;
    }
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * v0M5.4c (2026-07-25) — Draw a subtle gold ring above each settlement
 * where the player has an own Spy currently inside. Signals "you have
 * intel here" at a glance without opening the panel. Skipped when
 * playerFactionId is null (pre-menu smoke path) or the sim has no spies.
 *
 * Pulse the ring alpha slowly (~2s cycle) so it reads as active
 * without being flashy. Sits above the city sprite, below the battle-
 * icon z-layer.
 */
function drawSpyInsideIndicators(
  ctx: CanvasRenderingContext2D,
  renderState: RenderState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const { state, playerFactionId } = renderState;
  if (playerFactionId === null) return;
  if (!state.spies || state.spies.size === 0) return;

  // Build a set of settlement ids containing at least one own spy inside.
  const settlementsWithSpy = new Set<number>();
  for (const spy of state.spies.values()) {
    if (spy.ownerFactionId !== playerFactionId) continue;
    if (spy.insideSettlementId == null) continue;
    settlementsWithSpy.add(spy.insideSettlementId as unknown as number);
  }
  if (settlementsWithSpy.size === 0) return;

  const z = pxPerCell(camera);
  const majorRadius = cityIconRadius(camera);
  const ringRadius = majorRadius + Math.max(3, Math.round(z * 0.4));
  const t = performance.now() / 1000;
  const alpha = 0.55 + 0.35 * (0.5 + 0.5 * Math.sin(t * Math.PI));

  ctx.save();
  ctx.strokeStyle = `rgba(244, 212, 122, ${alpha.toFixed(3)})`;
  ctx.lineWidth = 2;
  for (const settlementId of sortedIds(state.settlements)) {
    if (!settlementsWithSpy.has(settlementId as unknown as number)) continue;
    const city = state.settlements.get(settlementId);
    if (!city) continue;
    const s = worldToScreen(camera, canvasW, canvasH, city.cell.x + 0.5, city.cell.y + 0.5);
    if (s.x < -30 || s.x > canvasW + 30 || s.y < -30 || s.y > canvasH + 30) continue;
    ctx.beginPath();
    ctx.arc(s.x, s.y, ringRadius, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * v0M12.3 (2026-07-26) — World-buildable sprite cache. Per-kind SVG
 * sprites loaded from `/assets/world-structures/<kind>.svg`; missing
 * files fall through to the v0M7.5 canvas glyphs so the mechanic
 * ships even without art. Load-once-then-cached — mirrors the
 * `ensureBattleIconLoaded` pattern.
 *
 * Michael-owed: source small silhouette icons from Game-Icons.net
 * (candidates in v0M12-close notes) and drop them at:
 *   public/assets/world-structures/road.svg
 *   public/assets/world-structures/watchtower.svg
 *   public/assets/world-structures/bridge.svg
 *   public/assets/world-structures/fort.svg
 */
const worldStructureSprites: Record<string, HTMLImageElement | null> = {};
const worldStructureLoadStarted: Record<string, boolean> = {};
function ensureWorldStructureSprite(kind: string): HTMLImageElement | null {
  if (worldStructureLoadStarted[kind]) return worldStructureSprites[kind] ?? null;
  worldStructureLoadStarted[kind] = true;
  const img = new Image();
  img.onload = () => { worldStructureSprites[kind] = img; };
  img.onerror = () => { worldStructureSprites[kind] = null; };
  img.src = `/assets/world-structures/${kind}.svg`;
  return null;
}

/**
 * v0M7.5 (2026-07-25) — Draw every WorldStructure per MAP.md §9.
 * Per-kind coloured glyphs at v0M7 (canvas fallback):
 *   Road stone chip, Watch Tower pale-gold triangle with owner ring,
 *   Bridge pale span, Fort dark-brown crenellated square with owner ring.
 *
 * v0M12.3 (2026-07-26) — sprite-first: attempt to draw the
 * `world-structures/<kind>.svg` sprite; fall through to canvas glyph
 * when the sprite hasn't loaded yet or is missing on disk. Owner-ring
 * still painted separately so the faction colour reads regardless of
 * sprite style.
 */
function drawWorldStructures(
  ctx: CanvasRenderingContext2D,
  renderState: RenderState,
  camera: Camera,
  canvasW: number,
  canvasH: number,
): void {
  const { state } = renderState;
  if (!state.worldStructures || state.worldStructures.size === 0) return;
  const z = pxPerCell(camera);
  const glyphSize = Math.max(6, Math.min(14, Math.round(z * 0.9)));

  ctx.save();
  for (const wsId of sortedIds(state.worldStructures)) {
    const ws = state.worldStructures.get(wsId as import('../../sim/state').WorldStructureId);
    if (!ws) continue;
    const s = worldToScreen(camera, canvasW, canvasH, ws.cell.x + 0.5, ws.cell.y + 0.5);
    if (s.x < -30 || s.x > canvasW + 30 || s.y < -30 || s.y > canvasH + 30) continue;

    // v0M7.5 (2026-07-25) — Owner ring uses the canonical faction
    // colour per Michael's ask ("less ambiguous for multiplayer").
    // Neutral / unowned falls to a muted grey. Matches drawArmies
    // colour path — `getFactionById(id).color` returns [r,g,b];
    // formatted into a CSS string.
    let ownerColor = '#aaaaaa';
    if (ws.ownerFactionId != null) {
      const faction = state.factions.get(ws.ownerFactionId);
      if (faction) {
        const isNeutral = faction.teamId === NEUTRAL_TEAM_ID;
        if (!isNeutral) {
          const def = getFactionById(faction.id as unknown as number);
          const c = def.color;
          ownerColor = `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
        }
      }
    }

    ctx.save();
    ctx.translate(s.x, s.y);

    // v0M12.3 (2026-07-26) — sprite-first path. Kick the load on first
    // sight of each kind; use the sprite once loaded. Owner ring
    // (kinds with owned semantics: watchtower + fort) painted UNDER
    // the sprite so the faction colour reads through a transparent
    // silhouette. Road + Bridge have no owner ring per §9.2 (Road is
    // permanent-unowned; Bridge is informational-only ownership).
    const sprite = ensureWorldStructureSprite(ws.kind);
    if (sprite !== null) {
      const spriteSize = glyphSize * 1.6;
      if (ws.kind === 'watchtower' || ws.kind === 'fort') {
        ctx.fillStyle = ownerColor;
        ctx.beginPath();
        ctx.arc(0, 0, spriteSize * 0.55, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.drawImage(sprite, -spriteSize / 2, -spriteSize / 2, spriteSize, spriteSize);
      ctx.restore();
      continue;
    }

    // Canvas-glyph fallback (v0M7.5 originals). Fires when the SVG
    // hasn't loaded yet OR is missing on disk.
    switch (ws.kind) {
      case 'road': {
        ctx.fillStyle = '#3a2c1a';
        ctx.strokeStyle = 'rgba(0,0,0,0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(0, 0, glyphSize * 0.35, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        break;
      }
      case 'watchtower': {
        ctx.fillStyle = '#f0c66a';
        ctx.strokeStyle = ownerColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, -glyphSize * 0.7);
        ctx.lineTo(glyphSize * 0.6, glyphSize * 0.5);
        ctx.lineTo(-glyphSize * 0.6, glyphSize * 0.5);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        break;
      }
      case 'bridge': {
        ctx.fillStyle = '#c9a86a';
        ctx.strokeStyle = 'rgba(0,0,0,0.6)';
        ctx.lineWidth = 1;
        const halfW = glyphSize * 0.8;
        const halfH = glyphSize * 0.25;
        ctx.fillRect(-halfW, -halfH, halfW * 2, halfH * 2);
        ctx.strokeRect(-halfW, -halfH, halfW * 2, halfH * 2);
        break;
      }
      case 'fort': {
        ctx.fillStyle = '#5a4028';
        ctx.strokeStyle = ownerColor;
        ctx.lineWidth = 2;
        const half = glyphSize * 0.7;
        ctx.fillRect(-half, -half, half * 2, half * 2);
        ctx.strokeRect(-half, -half, half * 2, half * 2);
        const crenH = glyphSize * 0.25;
        const crenW = glyphSize * 0.3;
        ctx.fillStyle = '#3a281a';
        for (let i = 0; i < 3; i++) {
          const cx = -half + (i + 0.5) * (2 * half / 3) - crenW / 2;
          ctx.fillRect(cx, -half - crenH, crenW, crenH);
        }
        break;
      }
    }
    ctx.restore();
  }
  ctx.restore();
}
